import { shouldShowMiniTransport } from './miniTransportVisibility.ts';
import { useTransportBridgeSnapshot } from './useTransportBridge.ts';
import { useT } from '../../i18n/i18n.tsx';
import { formatDuration } from '../../lib/format.ts';
import './MiniTransportBar.css';

/** `ui/Icon.tsx`'s own `play`/`pause` paths carry a ~25% built-in margin inside their 24x24
 * viewBox (deliberate there, for optical parity with icons of very different silhouettes) — at
 * this button's small size that margin reads as the glyph floating away from the button's edge.
 * These are custom, filling to within ~3 units of the viewBox edge, and used ONLY here — the
 * shared icon set (and every other button that renders 'play'/'pause' through it) is untouched. */
const PLAY_PATH = 'M3 2 L3 22 L21 12 Z';
const PAUSE_PATH = 'M3 2h6v20H3zM15 2h6v20h-6z';

/**
 * MVP-353. A persistent play/pause + title strip, visible on every Console tab except Cockpit
 * itself (`shouldShowMiniTransport`) — Cockpit already has the full transport bar
 * (`OperatorConsole.tsx`'s `.operator-console__transport`) on screen. Reads/controls the ONE
 * shared `usePlaybackEngine` instance via `useTransportBridge.ts`'s bridge rather than opening a
 * second decoder — `App.tsx`'s `ConsoleShell` mounts the `<TransportBridgeProvider>` this reads
 * from. Renders inside `<StatusBar>`'s `center` slot (App.tsx) rather than its own row. Even
 * `size="sm"` (28px) filled the whole footer row and read as too heavy for a secondary, always-on
 * control, so `.mini-transport__play` (MiniTransportBar.css) shrinks the box further to 22px.
 */
export function MiniTransportBar({ view }: { view: string }) {
  const t = useT();
  const snapshot = useTransportBridgeSnapshot();
  if (!snapshot || !shouldShowMiniTransport(snapshot.title, view)) return null;
  const label = t(snapshot.isPlaying ? 'player.transport.pause' : 'player.transport.play');
  return (
    <div className="mini-transport" role="region" aria-label={t('app.miniTransport.regionLabel')}>
      <button
        type="button"
        className="icon-btn icon-btn--primary mini-transport__play"
        aria-label={label}
        title={label}
        onClick={() => snapshot.togglePlay()}
        disabled={snapshot.disabled}
      >
        <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={snapshot.isPlaying ? PAUSE_PATH : PLAY_PATH} />
        </svg>
      </button>
      <span className="mini-transport__title">{snapshot.title}</span>
      {snapshot.durationMs != null && (
        <span className="mini-transport__time tabular-nums">
          {formatDuration(snapshot.currentTimeMs)} / {formatDuration(snapshot.durationMs)}
        </span>
      )}
      {/* MVP-435: the only thing on this row that moves. The clock beside it already carries the
          same fact, but reading "is it advancing?" means holding two 12px digit strings a second
          apart in your head — from across a dim venue that is not a glance, it is a task. Four
          bars bouncing is. `aria-hidden` because the play/pause button's own label is the
          accessible statement of this state, and a second announcement of it would be noise. */}
      <span className="mini-transport__wave" data-playing={snapshot.isPlaying && !snapshot.disabled ? '' : undefined} aria-hidden="true">
        <i /><i /><i /><i />
      </span>
    </div>
  );
}
